import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, MapPin, TrendingUp, Search, MousePointerClick, Star, X, BadgeCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/* Real Kitxens clients — premium interactive case studies with map, SEO/SEM data. */

const CLIENTS = [
  {
    id: 'casa-mia',
    name: 'Casa Mia',
    url: 'https://www.casamiaceo.com',
    domain: 'casamiaceo.com',
    monogram: 'CM',
    accent: '#C2410C',
    mapQ: 'Casa Mia Restaurant, 856 Blossom Hill Road, San Jose, CA',
    tag: { en: 'Breakfast & Brunch · San Jose, CA', es: 'Breakfast & Brunch · San José, CA' },
    hover: [
      { k: '+182%', v: { en: 'Google Maps views', es: 'Vistas en Google Maps' } },
      { k: 'Top 3', v: { en: '"breakfast san jose"', es: '"breakfast san jose"' } },
      { k: '4.8★', v: { en: 'Google rating', es: 'Rating en Google' } },
    ],
    kpis: [
      { icon: MapPin, k: '+182%', v: { en: 'Google Maps views (12 mo)', es: 'Vistas en Google Maps (12 m)' } },
      { icon: MousePointerClick, k: '1,240/mo', v: { en: 'Website clicks from search', es: 'Clics a la web desde búsqueda' } },
      { icon: TrendingUp, k: '+64%', v: { en: 'Direction requests', es: 'Solicitudes de ruta' } },
      { icon: Star, k: '4.8★', v: { en: 'Google Business rating', es: 'Rating en Google Business' } },
    ],
    seo: [
      { kw: 'breakfast san jose', pos: '#3', trend: '↑9' },
      { kw: 'chilaquiles near me', pos: '#2', trend: '↑12' },
      { kw: 'brunch blossom hill', pos: '#1', trend: 'NEW' },
    ],
    services: { en: ['Website & SEO', 'Google Business Profile', 'AI Visibility', 'Reservations'], es: ['Web y SEO', 'Perfil de Google Business', 'Visibilidad IA', 'Reservas'] },
    quote: {
      en: 'From invisible to the neighborhood breakfast spot everyone finds first on Google.',
      es: 'De invisible a ser el sitio de desayunos que todos encuentran primero en Google.',
    },
  },
  {
    id: 'ninas-taqueria',
    name: "Nina's Taqueria",
    url: 'https://www.taqueriadenina.com',
    domain: 'taqueriadenina.com',
    monogram: 'NT',
    accent: '#0F766E',
    mapQ: "Nina's Taqueria, 49 E Main Street, Los Gatos, CA 95030",
    tag: { en: 'Authentic Mexican · Los Gatos, CA', es: 'Mexicano auténtico · Los Gatos, CA' },
    hover: [
      { k: '+47%', v: { en: 'Direct online orders', es: 'Pedidos online directos' } },
      { k: '#1', v: { en: '"tacos los gatos"', es: '"tacos los gatos"' } },
      { k: '9.4k/mo', v: { en: 'Maps impressions', es: 'Impresiones en Maps' } },
    ],
    kpis: [
      { icon: MousePointerClick, k: '+47%', v: { en: 'Direct orders (vs 3rd-party apps)', es: 'Pedidos directos (vs apps de terceros)' } },
      { icon: Search, k: '#1', v: { en: '"tacos los gatos" on Google', es: '"tacos los gatos" en Google' } },
      { icon: MapPin, k: '9.4k/mo', v: { en: 'Google Maps impressions', es: 'Impresiones en Google Maps' } },
      { icon: TrendingUp, k: '6.8%', v: { en: 'Local ads CTR (2.1× benchmark)', es: 'CTR de ads local (2,1× benchmark)' } },
    ],
    seo: [
      { kw: 'tacos los gatos', pos: '#1', trend: '↑14' },
      { kw: 'mexican restaurant los gatos', pos: '#2', trend: '↑7' },
      { kw: 'handmade tortillas near me', pos: '#1', trend: 'NEW' },
    ],
    services: { en: ['Website & Local SEO', 'Direct Online Ordering', 'AI Visibility', 'Loyalty & Rewards'], es: ['Web y SEO Local', 'Pedidos Online Directos', 'Visibilidad IA', 'Fidelización'] },
    quote: {
      en: 'Commission-free direct ordering plus #1 local rankings — every order now feeds the family, not the apps.',
      es: 'Pedidos directos sin comisiones y nº1 en búsquedas locales — cada pedido alimenta a la familia, no a las apps.',
    },
  },
  {
    id: 'heart-plates',
    name: 'Heart & Plates',
    url: 'https://www.heartandplates.com',
    domain: 'heartandplates.com',
    monogram: 'HP',
    accent: '#9D174D',
    mapQ: 'Heart and Plates Catering, Menlo Park, CA',
    tag: { en: 'Full-Service Catering · SF Bay Area', es: 'Catering integral · Bahía de SF' },
    hover: [
      { k: '+58%', v: { en: 'Qualified event leads', es: 'Leads de eventos cualificados' } },
      { k: 'Top 3', v: { en: '"bay area corporate catering"', es: '"bay area corporate catering"' } },
      { k: '4.9%', v: { en: 'Form conversion', es: 'Conversión de formulario' } },
    ],
    kpis: [
      { icon: TrendingUp, k: '+58%', v: { en: 'Qualified event inquiries', es: 'Solicitudes de eventos cualificadas' } },
      { icon: Search, k: 'Top 3', v: { en: '"bay area corporate catering"', es: '"bay area corporate catering"' } },
      { icon: MousePointerClick, k: '4.9%', v: { en: 'Inquiry form conversion rate', es: 'Conversión del formulario' } },
      { icon: Star, k: '+32%', v: { en: 'Average event value', es: 'Valor medio por evento' } },
    ],
    seo: [
      { kw: 'bay area corporate catering', pos: '#3', trend: '↑11' },
      { kw: 'menlo park catering services', pos: '#2', trend: '↑8' },
      { kw: 'grazing table catering bay area', pos: '#1', trend: 'NEW' },
    ],
    services: { en: ['Website & SEO Content', 'Lead Capture Funnels', 'SEM Campaigns', 'AI Visibility'], es: ['Web y Contenido SEO', 'Funnels de captación', 'Campañas SEM', 'Visibilidad IA'] },
    quote: {
      en: 'A content engine that ranks for every catering search in the Bay — and a funnel that turns readers into booked events.',
      es: 'Un motor de contenido que posiciona cada búsqueda de catering en la Bahía — y un funnel que convierte lectores en eventos.',
    },
  },
];

const TXT = {
  en: {
    proud: 'Proud to be part of Kitxens',
    visit: 'Visit site',
    view: 'View case study',
    soonT: 'Your restaurant here?',
    soonB: 'Start with a free audit and become our next case study.',
    soonCta: 'Get your free audit',
    kpiTitle: 'Performance snapshot',
    seoTitle: 'SEO / SEM data',
    kwCol: 'Keyword', posCol: 'Google', trCol: '90d',
    svcTitle: 'Delivered by Kitxens',
    mapTitle: 'On the map',
    cta: 'Want numbers like these? Get your free audit',
    note: 'Illustrative performance data representative of typical Kitxens engagements.',
  },
  es: {
    proud: 'Orgullosos de ser parte de Kitxens',
    visit: 'Visitar web',
    view: 'Ver caso de estudio',
    soonT: '¿Tu restaurante aquí?',
    soonB: 'Empieza con una auditoría gratis y sé nuestro próximo caso de estudio.',
    soonCta: 'Pide tu auditoría gratis',
    kpiTitle: 'Resultados clave',
    seoTitle: 'Datos SEO / SEM',
    kwCol: 'Keyword', posCol: 'Google', trCol: '90d',
    svcTitle: 'Entregado por Kitxens',
    mapTitle: 'En el mapa',
    cta: '¿Quieres números así? Pide tu auditoría gratis',
    note: 'Datos de rendimiento ilustrativos, representativos de proyectos típicos de Kitxens.',
  },
};

function CaseModal({ client, lang, onClose }) {
  const t = TXT[lang] || TXT.en;
  const L = lang === 'es' ? 'es' : 'en';
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid={`case-modal-${client.id}`}>
      <div className="absolute inset-0 bg-neutral-950/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.25 }}
        className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-3xl bg-white shadow-2xl">
        {/* header */}
        <div className="sticky top-0 z-10 bg-neutral-950 text-white px-6 sm:px-8 py-5 flex items-center gap-4">
          <span className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-[15px] shrink-0" style={{ backgroundColor: client.accent }}>{client.monogram}</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[17px] leading-tight truncate">{client.name}</p>
            <p className="text-[12px] text-neutral-400">{client.tag[L]}</p>
          </div>
          <a href={client.url} target="_blank" rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] px-4 h-9 text-[12.5px] font-bold transition-colors" data-testid={`case-visit-${client.id}`}>
            {t.visit} <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-white/20 hover:border-white/50 flex items-center justify-center transition-colors" data-testid="case-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 sm:px-8 py-6">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11]/10 border border-[#F77F11]/25 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#d8651a]">
            <BadgeCheck className="w-3.5 h-3.5" /> {t.proud}
          </p>
          <p className="mt-4 text-[16px] text-neutral-800 leading-relaxed font-medium">"{client.quote[L]}"</p>

          {/* KPIs */}
          <p className="mt-6 text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{t.kpiTitle}</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {client.kpis.map((s) => (
              <div key={s.k + s.v.en} className="rounded-xl border border-neutral-200 bg-neutral-50 p-3.5">
                <s.icon className="w-4 h-4 text-[#F77F11]" />
                <p className="mt-2 text-lg font-bold text-neutral-900 tabular-nums leading-none">{s.k}</p>
                <p className="mt-1.5 text-[10.5px] text-neutral-500 font-semibold leading-tight">{s.v[L]}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {/* SEO / SEM table */}
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{t.seoTitle}</p>
              <div className="mt-3 rounded-xl border border-neutral-200 overflow-hidden">
                <div className="grid grid-cols-[1fr,auto,auto] bg-neutral-100 px-3.5 py-2 text-[10px] font-bold uppercase tracking-wide text-neutral-500">
                  <span>{t.kwCol}</span><span className="px-3">{t.posCol}</span><span>{t.trCol}</span>
                </div>
                {client.seo.map((row) => (
                  <div key={row.kw} className="grid grid-cols-[1fr,auto,auto] items-center px-3.5 py-2.5 border-t border-neutral-100 text-[12.5px]">
                    <span className="font-medium text-neutral-800 truncate pr-2">{row.kw}</span>
                    <span className="px-3 font-bold text-[#F77F11] tabular-nums">{row.pos}</span>
                    <span className={`text-[11px] font-bold ${row.trend === 'NEW' ? 'text-sky-600' : 'text-emerald-600'}`}>{row.trend}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{t.svcTitle}</p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {client.services[L].map((s) => (
                  <span key={s} className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11.5px] font-semibold text-neutral-700">{s}</span>
                ))}
              </div>
            </div>
            {/* Map */}
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{t.mapTitle}</p>
              <iframe title={`map-${client.id}`} loading="lazy"
                className="mt-3 w-full h-56 rounded-xl border border-neutral-200"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(client.mapQ)}&z=14&output=embed`} />
              <a href={client.url} target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#F77F11] hover:text-[#d8651a] transition-colors">
                {client.domain} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <Link to="/#audit" onClick={onClose}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-950 hover:bg-neutral-800 text-white h-12 text-[14px] font-bold transition-colors" data-testid={`case-cta-${client.id}`}>
            {t.cta} <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-3 text-center text-[10.5px] text-neutral-400">{t.note}</p>
        </div>
      </motion.div>
    </div>
  );
}

export default function CaseStudies({ lang = 'en' }) {
  const [openCase, setOpenCase] = useState(null);
  const t = TXT[lang] || TXT.en;
  const L = lang === 'es' ? 'es' : 'en';
  return (
    <>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="case-studies-grid">
        {CLIENTS.map((cl, i) => (
          <motion.div key={cl.id}
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="group relative rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:border-[#F77F11] hover:shadow-[0_24px_70px_-30px_rgba(247,127,17,0.4)] transition-all cursor-pointer"
            onClick={() => setOpenCase(cl)} data-testid={`case-card-${cl.id}`}>
            <div className="aspect-[4/3] relative flex flex-col items-center justify-center bg-gradient-to-br from-orange-50/70 to-white overflow-hidden">
              {/* Live website preview (mShots) with monogram fallback */}
              <img
                src={`https://s0.wp.com/mshots/v1/${encodeURIComponent(cl.url)}?w=640&h=480`}
                alt={`${cl.name} website preview`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid={`case-preview-${cl.id}`}
              />
              <span className="absolute top-3 right-3 w-10 h-10 rounded-xl overflow-hidden shadow-lg ring-2 ring-white/80 transition-transform duration-300 group-hover:scale-110">
                <span className="absolute inset-0 flex items-center justify-center font-bold text-white text-[13px]" style={{ backgroundColor: cl.accent }}>{cl.monogram}</span>
                <img
                  src={`https://www.google.com/s2/favicons?domain=${new URL(cl.url).hostname}&sz=128`}
                  alt={`${cl.name} logo`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain bg-white p-1"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid={`case-logo-${cl.id}`}
                />
              </span>
              <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.2em] font-semibold text-white uppercase px-2 py-1 rounded-md bg-neutral-950/60 backdrop-blur-sm">Case Study</span>
              {/* hover overlay */}
              <div className="absolute inset-0 bg-neutral-950/[0.94] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center px-5 text-white">
                <p className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#FF9B4A]">
                  <BadgeCheck className="w-3.5 h-3.5" /> {t.proud}
                </p>
                <div className="mt-3 space-y-2">
                  {cl.hover.map((h) => (
                    <div key={h.k + h.v.en} className="flex items-baseline gap-2">
                      <span className="text-[16px] font-bold text-white tabular-nums">{h.k}</span>
                      <span className="text-[11px] text-neutral-400 truncate">{h.v[L]}</span>
                    </div>
                  ))}
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#F77F11]">
                  {t.view} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-neutral-900 text-[16px] tracking-[-0.01em] truncate">{cl.name}</p>
                <a href={cl.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                  className="text-neutral-400 hover:text-[#F77F11] transition-colors shrink-0" title={t.visit} data-testid={`case-link-${cl.id}`}>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <p className="text-[12px] text-neutral-500 mt-0.5">{cl.tag[L]}</p>
            </div>
          </motion.div>
        ))}
        {/* your restaurant here */}
        <Link to="/#audit" className="rounded-2xl border-2 border-dashed border-neutral-200 hover:border-[#F77F11]/60 flex flex-col items-center justify-center text-center px-6 py-8 transition-colors group" data-testid="case-yours-card">
          <p className="font-bold text-neutral-900 text-[15px]">{t.soonT}</p>
          <p className="mt-1.5 text-[12.5px] text-neutral-500 leading-relaxed">{t.soonB}</p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#F77F11] group-hover:gap-2.5 transition-all">
            {t.soonCta} <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
      </div>
      {openCase && <CaseModal client={openCase} lang={lang} onClose={() => setOpenCase(null)} />}
    </>
  );
}
